import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';

import {useStore} from 'effector-react';

import {useFieldArray, useForm} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Row} from 'react-table';
import {toast} from 'react-toastify';

import {ReactComponent as ScanIcon} from '../../../assets/svg/scan.svg';
import {I18N_NS} from '../../../core/constants/names';
import {getAdapter} from '../../../init';
import {$allowedActionsForInvoice} from '../../../models/common';
import {
    $initalLineValuesHolder,
    $invoiceCreationPage,
    $invoiceLineChargeData,
    $invoiceLinesErrors,
    $invoiceLinesHolder,
    $invoiceLinesHolderCodesTotal,
    $invoiceLineValues,
    addInvoiceLineValues,
    invoiceCreationPage,
    setInitalLineValuesHolder,
    setInvoiceLineChargeData,
    setInvoiceLinesHolder,
    updateAllDataFromInvoiceLinesHolder,
    updateInvoiceLinesHolder,
} from '../../../models/create';
import {$currentScannedGtin, setCurrentScannedGtin} from '../../../models/details';
import {$isNG} from '../../../models/utils';
import {CUSTOMER_PACKAGE_TYPES, PackageTypes} from '../../../types/gtins';
import {InvocieLineHolder, InvoiceLine} from '../../../types/invoices';
import {CodeResponseType} from '../../../types/validation';

import {ControlSection} from '../../ControlSection';

import {isStandartCategory, VAT_CATEGORY} from '../../helpers/consts';
import {useCodeScanner} from '../../hooks/useCodeScanner';
import {useCombinedData2} from '../../hooks/useCombinedData2';

import {useInvoiceLinesColumns} from '../../hooks/useInvoiceLinesColumns';
import {useLineCalculations} from '../../hooks/useLineCalculations';
import {
    ParsedDataExpected,
    XlsxFileInvalidRow,
    XlsxFileParsedData,
} from '../../hooks/useParseInvoiceLinesXlsx';
import {Button, ButtonVariant} from '../../ui-kit/Button';
import {Icon} from '../../ui-kit/Icon';
import {mapTableColumns, Table} from '../../ui-kit/Table2.0';

import {ModalDuplicateLines} from './components/ModalDuplicateLines';
import {ModalFormComponent, ModalFormReadonly} from './components/ModalForm';
import {ModalParseLinesFromXlsx} from './components/ModalParseLinesFromXlsx';
import {
    SimpleTable,
    SimpleTablePagination,
    PaginationControlsProps,
} from './components/SimpleTable';
import {
    AddInvoiceLineForm,
    ButtonGroup,
    Container,
    HeaderGroupItem,
    Headline,
    Highlighted,
    RequiredTag,
    Root,
    SectionDescription,
} from './styled';
import {createValidationContext, lineSchema} from './validation';

interface InvoiceLineData extends ParsedDataExpected {
    serviceCategory: {label: string; value: string};
    gtin: string;
    identityCode: string;
    productName: string;
    packagingType: PackageTypes;
}

export const fieldsToAppend: InvoiceLine = {
    description: '',
    code: '',
    gtin: '',
    qty: '',
    price: '',
    lines: '',
    vatCategory: {label: '', value: ''},
    vat: '',
    lineAmountWVat: '',
    default: true,
    isHsn: 'HS',
    serviceCategory: undefined,
    copyActions: {
        allowToCopy: false,
        allowToDuplicateDown: false,
        allowToPaste: true,
        allowToDelete: true,
    },
    isMarkedItem: false,
};

export const processInvoiceLinesWithCombinedCodes = (lines: InvocieLineHolder[]): InvoiceLine[] => {
    const res = processLinesRecursive(lines, undefined, 1);
    return res;
};

function processLinesRecursive(
    lines: InvocieLineHolder[],
    parentCode: string | undefined,
    depth: number,
): InvoiceLine[] {
    const toLowerAndTrim = (str: string) => str.toLowerCase().trim();
    const result: InvoiceLine[] = [];
    const index = `row-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2)}`;

    for (const line of lines) {
        const addCharges = line.charges
            ? toLowerAndTrim(line.charges.isCharge) === 'true'
                ? Number(line.charges.amountXaf)
                : -Number(line.charges.amountXaf)
            : 0;

        const qtyNum = 1;
        const lineExtension = qtyNum * (Number(line.price) || 0) + addCharges;
        const vatRate = Number(line.vat) || 0;
        const lineAmountWVatNum = (vatRate / 100) * lineExtension + lineExtension;

        const baseMeta = {level: depth, parent: parentCode};
        const linesStr = lineExtension.toFixed(2);
        const lineAmountWVatStr = lineAmountWVatNum.toFixed(2);

        if (!line.combinedCodes || line.combinedCodes.length === 0) {
            result.push({
                ...line,
                qty: line.qty ?? '1',
                lines: line.lines ?? linesStr,
                lineAmountWVat: line.lineAmountWVat ?? lineAmountWVatStr,
                ...baseMeta,
            } as InvoiceLine);

            if (line.subRows?.length) {
                const childParentCode = line.code ?? parentCode;
                result.push(...processLinesRecursive(line.subRows, childParentCode, depth + 1));
            }
            continue;
        }

        const {combinedCodes, subRows, ...baseLine} = line;
        const [firstCode, ...remainingCodes] = combinedCodes;
        const childParentCode = firstCode ?? line.code ?? parentCode;

        result.push({
            ...baseLine,
            code: firstCode,
            qty: '1',
            lines: linesStr,
            lineAmountWVat: lineAmountWVatStr,
            ...(remainingCodes.length > 0 && {combinedCodeId: index}),
            ...baseMeta,
        } as InvoiceLine);

        for (const code of remainingCodes) {
            result.push({
                ...baseLine,
                code,
                qty: '1',
                lines: linesStr,
                lineAmountWVat: lineAmountWVatStr,
                combinedCodeId: index,
                ...baseMeta,
            } as InvoiceLine);
        }

        if (subRows?.length) {
            result.push(...processLinesRecursive(subRows, childParentCode, depth + 1));
        }
        // index++;
    }

    return result;
}

export const InvoiceLinesTab = () => {
    const allowedActionsForInvoice = useStore($allowedActionsForInvoice);
    const currentPageIndex = useStore($invoiceCreationPage);
    const invoiceLineValues = useStore($invoiceLineValues);
    const invoiceLinesHolder = useStore($invoiceLinesHolder);
    const invoiceLineChargeData = useStore($invoiceLineChargeData);
    const initalLineValuesHolder = useStore($initalLineValuesHolder);
    const [allowSubmitButton, setAllowSubmitButton] = useState<boolean>(false);
    const [xlsxErrors, setXlsxErrors] = useState<XlsxFileInvalidRow>({});
    const [combineByGtin, setCombineByGtin] = useState(false);
    const [dataParsingPending, setDataParsingPending] = useState(false);
    const invoiceLinesHolderCodesTotal = useStore($invoiceLinesHolderCodesTotal);

    const [paginationProps, setPaginationProps] = useState<PaginationControlsProps | null>(null);

    const {t: translation} = useTranslation(I18N_NS);
    const GEN_INFO = 'pages.documents.invoices.create.invoiceLines';
    const t = (key: string, vars?: Record<string, any>) =>
        translation(`${GEN_INFO}.${key}`, vars ?? {});

    const [scanShow, setScanShow] = React.useState(false);
    const [scanPending, setScanPending] = React.useState(false);

    const resetCombineByGtin = useCallback(() => {
        setCurrentScannedGtin(null);
        setCombineByGtin(false);
    }, []);

    useEffect(() => {
        if (!combineByGtin) {
            setCurrentScannedGtin(null);
        }
    }, [combineByGtin]);

    const convertScanToInvoiceView = (
        scan: CodeResponseType['info'],
    ): Partial<InvoiceLine & {codeGtin: string}> => ({
        code: scan.identityCode,
        gtin: scan.gtin,
        qty: '1',
        description: scan.productName,
        codeGtin: scan.identityCode,

        price: '',
        lines: '',
        vatCategory: {label: '', value: ''},
        vat: '',
        lineAmountWVat: '',
        isHsn: 'HS',
        serviceCategory: {label: scan.hsnDescription ?? scan.hsnCode, value: scan.hsnCode},
        copyActions: {
            allowToCopy: false,
            allowToDuplicateDown: false,
            allowToPaste: true,
            allowToDelete: true,
        },
        isMarkedItem: true,
    });

    const DEFAULT_LINE_VALUES: Partial<InvoiceLine> = {
        description: '',
        code: '',
        gtin: '',
        qty: '',
        isHsn: 'HS',
        serviceCategory: {label: '', value: ''},
        isMarkedItem: false,
        packagingType: undefined,
        level: 1,
        copyActions: {
            allowToCopy: false,
            allowToDuplicateDown: false,
            allowToPaste: false,
            allowToDelete: true,
        },
    };

    const handleEnterFromLine = (response: CodeResponseType['info']) => {
        if (response.packagingType && !CUSTOMER_PACKAGE_TYPES.includes(response.packagingType)) {
            clearCodeAndDescription2();
            toast.error(t('packagingTypeNotAllowed'));
            return;
        }

        const {description, code, gtin, serviceCategory} = convertScanToInvoiceView(response);
        setInitalLineValuesHolder({
            ...DEFAULT_LINE_VALUES,
            ...(code &&
                gtin && {
                    qty: '1',
                    description,
                    packagingType: response.packagingType,
                    code,
                    gtin,
                    serviceCategory,
                    isMarkedItem: true,
                    copyActions: {
                        allowToCopy: false,
                        allowToDuplicateDown: false,
                        allowToPaste: false,
                        allowToDelete: true,
                    },
                }),
        });
        setValue('description', description);
        setValue('qty', '1');
        setValue('isHsn', 'HS');
        setValue('serviceCategory', serviceCategory);
    };

    const clearCodeAndDescription2 = () => {
        setValue('codeGtin', '');
        setValue('description', '');
        setValue('serviceCategory', '');
        setInitalLineValuesHolder(DEFAULT_LINE_VALUES);
    };

    const {onEnterCode, codeGtinHolder} = useCodeScanner({
        combineByGtin,
        handleEnterFromLine,
        clearCodeAndDescription: clearCodeAndDescription2,
        setScanPending,
    });

    const formContext = useMemo(() => createValidationContext(), []);

    const {
        control,
        handleSubmit,
        trigger,
        setValue,
        getValues,
        reset,
        formState: {errors},
    } = useForm({
        defaultValues: {
            description: '',
            qty: '',
            isHsn: 'HS',
            serviceCategory: '',
            price: '',
            lines: '',
            vatCategory: undefined,
            vat: '',
            lineAmountWVat: '',
            codeGtin: '',
        },
        resolver: yupResolver(lineSchema),
        context: formContext,
    });

    const {fields, remove, append} = useFieldArray<Partial<InvoiceLine> & {default?: boolean}>({
        control,
        name: 'test',
    });

    useEffect(() => {
        if (fields.length > 1 && fields[0].default) remove(0);
        if (!fields.length && allowedActionsForInvoice.edit) {
            append(fieldsToAppend);
        }
    }, [fields]);

    const onSubmit = (data: any) => {
        const isDouble = initalLineValuesHolder?.code
            ? $invoiceLinesHolderCodesTotal.getState().includes(initalLineValuesHolder.code)
            : false;

        if (isDouble) {
            toast.error(
                t('xlsxModal.errors.markingCodeExists', {
                    markingCode: initalLineValuesHolder?.code,
                }),
            );
            return;
        }

        setInvoiceLinesHolder([
            {
                ...data,
                packagingType: initalLineValuesHolder?.packagingType ?? '',
                isMarkedItem: !initalLineValuesHolder ? false : initalLineValuesHolder.isMarkedItem,
                copyActions: initalLineValuesHolder
                    ? initalLineValuesHolder?.copyActions
                    : {
                          allowToCopy: true,
                          allowToDuplicateDown: false,
                          allowToPaste: false,
                          allowToDelete: true,
                      },
                ...(invoiceLineChargeData && {charges: invoiceLineChargeData}),
                ...(initalLineValuesHolder && {
                    code: initalLineValuesHolder.code,
                    gtin: initalLineValuesHolder.gtin,
                }),
            },
        ]);

        reset({
            description: '',
            qty: '',
            isHsn: 'HS',
            serviceCategory: '',
            price: '',
            lines: '',
            vatCategory: undefined,
            vat: '',
            lineAmountWVat: '',
            codeGtin: '',
        });
        codeGtinHolder.current = '';
        setInitalLineValuesHolder(DEFAULT_LINE_VALUES);
        setInvoiceLineChargeData(null);
    };

    const {columnsSettings} = useInvoiceLinesColumns();
    const columns = useMemo(() => mapTableColumns(columnsSettings), [columnsSettings]);

    const {mappedFields2} = useCombinedData2({
        control,
        t: translation,
        onEnterCode,
        trigger,
        getValues,
        setValue,
        errors,
    });

    const [skipPageReset, setSkipPageReset] = useState(false);

    const {combineValues, calculateLineAmountWVat} = useLineCalculations();
    const updateMyData = (
        columnId: string,
        value: string | object | null,
        depth: {step: number; id: string},
    ) => {
        setSkipPageReset(true);
        const {step, id} = depth;
        updateInvoiceLinesHolder({
            data: {columnId, value: value as any},
            depth: {step, id},
        });
        if (columnId === 'price' || columnId === 'qty') {
            combineValues(id);
            calculateLineAmountWVat(id);
        }
        if (columnId === 'vatCategory' || columnId === 'vat') {
            calculateLineAmountWVat(id, columnId);
        }
    };

    useEffect(() => {
        setSkipPageReset(false);
    }, [invoiceLinesHolder]);

    const mappedData = useMemo(() => invoiceLinesHolder, [invoiceLinesHolder]);

    const isNextButtonDisabled = useMemo(
        () => !invoiceLineValues?.length || $invoiceLinesErrors.getState().length > 0,
        [invoiceLineValues, $invoiceLinesErrors],
    );

    const [showXlsxModal, setShowXlsxModal] = useState(false);
    const onCloseXlsxModal = () => {
        setShowXlsxModal(false);
        resetCombineByGtin();
    };
    function onShowXlsxModal() {
        setShowXlsxModal(true);
    }

    return (
        <Root>
            <ModalFormComponent getValues={getValues} setValue={setValue} trigger={trigger} />
            <ModalFormReadonly />
            <ModalDuplicateLines />
            <Container>
                <Headline>
                    <h3>
                        {t('invoiceLines')}
                        {allowedActionsForInvoice.edit && (
                            <Highlighted>
                                {`(${$invoiceLinesHolder.getState().length ?? 0} ${t(
                                    'itemsAdded',
                                )})`}
                            </Highlighted>
                        )}
                    </h3>
                    <SectionDescription>
                        <span>{!allowedActionsForInvoice.edit ? '' : t('addGoods.1')}</span>
                    </SectionDescription>
                    <ButtonGroup
                        style={$isNG.getState() ? {justifyContent: 'flex-end'} : undefined}
                    ></ButtonGroup>
                </Headline>
                <AddInvoiceLineForm onSubmit={handleSubmit(onSubmit)}>
                    {allowedActionsForInvoice.edit && (
                        <Table
                            hoverableRow={false}
                            tableViewExtraSettings={{
                                tdThPadding: '5px 10px',
                                overflowX: 'visible',
                            }}
                            cellContainerSettings="width: 100%; height: 60px; align-items: flex-end;"
                            hideSetting={true}
                            isLoading={false}
                            columns={columns}
                            data={mappedFields2}
                        />
                    )}

                    {allowedActionsForInvoice.edit && (
                        <Button
                            style={{width: 'fit-content', position: 'sticky', left: 0}}
                            size="sm"
                            variant={ButtonVariant.Secondary}
                            disabled={allowSubmitButton}
                            type="submit"
                        >
                            {translation('pages.documents.invoices.create.buttons.addToInvoice')}
                        </Button>
                    )}

                    <SimpleTable
                        hideHeaders={allowedActionsForInvoice.edit}
                        columns={columns}
                        data={mappedData}
                        onPaginationReady={setPaginationProps}
                        updateMyData={updateMyData}
                        skipPageReset={skipPageReset}
                        renderExpandedRow={true}
                        getRowCanExpand={(row) => {
                            const {packagingType} = row.original as InvocieLineHolder;
                            return packagingType === PackageTypes.BOX;
                        }}
                        // showExpandableColumn={false}
                        expandableColumnId="lineId"
                        tableViewExtraSettings={{
                            tdThPadding: '5px 10px',
                            overflowX: 'visible',
                        }}
                        cellContainerSettings="width: 100%; height: 60px; align-items: flex-end;"
                    />
                </AddInvoiceLineForm>
                {paginationProps && mappedData.length > 0 && (
                    <div>
                        <SimpleTablePagination {...paginationProps} />
                    </div>
                )}
            </Container>
            <ControlSection
                nextIsDisabled={false}
                onSubmit={(nextIndex: number) => {
                    if (!invoiceLinesHolder.length) {
                        toast.error(
                            <div>
                                <strong>Errors</strong>
                                <ul style={{margin: '6px 0'}}>
                                    <li>{t('errors.noLines')}</li>
                                </ul>
                            </div>,
                        );
                        return;
                    }

                    if ($invoiceLinesErrors.getState().length) {
                        const limitErrors = [...$invoiceLinesErrors.getState()];
                        limitErrors.length > 5 ? (limitErrors.length = 5) : limitErrors;
                        toast.error(
                            <div>
                                <strong>Errors</strong>
                                <strong>{t('errors.emptyFields')}</strong>
                                <ul style={{margin: '6px 0'}}>
                                    {limitErrors.map((e) => {
                                        const {index, fields} = e;
                                        return (
                                            <li key={index}>
                                                {t('errors.line')} {index}: {fields.join(', ')}
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>,
                        );
                        return;
                    }

                    const processedLines = processInvoiceLinesWithCombinedCodes(invoiceLinesHolder);

                    addInvoiceLineValues(processedLines);
                    invoiceCreationPage(nextIndex);
                }}
                nextIndex={currentPageIndex + 1}
            />
        </Root>
    );
};
